<script lang="ts">
  import type { LogEntry } from '../../lib/labs';
  import { formatEntry } from '../../lib/labs';
  import { highlightLine } from '../swift-highlight';

  /**
   * 記録欄（components.md §3.2: `<ol aria-label="記録された Swift" aria-live="polite">`）。
   * 宣言の後に、操作ごとの Swift を 1 行ずつ積む。先頭の文字（追加・取り出し…）は動きを減らす設定でも状態の変化を伝える。
   */
  interface Props {
    declarations: string[];
    entries: readonly LogEntry[];
    /** コピーする本文。 */
    code: string;
    /** エディタへ送る（段階 3 へ）。 */
    onsend?: (() => void) | undefined;
    /** 最後に足す確認の行（`print(stack)  // [1, 3]`）。 */
    footer?: string | null;
  }

  let { declarations, entries, code, onsend, footer = null }: Props = $props();
  let status = $state('');

  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      status = 'コードをコピーしました。';
    } catch {
      status = 'コピーできませんでした。記録の行を選んでコピーできます。';
    }
  }
</script>

<div class="log-panel" data-testid="lab-log">
  <div class="code log-code">
    <ol class="log" aria-label="記録された Swift" aria-live="polite">
      {#each declarations as d (d)}
        <li><span class="op" aria-hidden="true"></span><code>{@render line(d)}</code></li>
      {/each}
      {#each entries as e, i (i)}
        <li class:new={i === entries.length - 1}>
          <span class="op">{e.tag}</span><code>{@render line(formatEntry(e))}</code>
        </li>
      {/each}
      {#if footer}
        <li class="footer">
          <span class="op" aria-hidden="true"></span><code>{@render line(footer)}</code>
        </li>
      {/if}
    </ol>
  </div>
  <div class="actions">
    <button type="button" class="btn btn-sm" onclick={copy} data-testid="lab-copy"
      >コードをコピー</button
    >
    {#if onsend}
      <button type="button" class="btn btn-sm btn-primary" onclick={onsend} data-testid="lab-send"
        >エディタへ送る</button
      >
    {/if}
    <span class="status" role="status">{status}</span>
  </div>
</div>

{#snippet line(text: string)}{#each highlightLine(text) as t, j (j)}{#if t.cls}<span class={t.cls}
        >{t.text}</span
      >{:else}{t.text}{/if}{/each}{/snippet}

<style>
  .log-panel {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .log-code {
    padding: 0;
    overflow-x: auto;
    max-height: 26rem;
    overflow-y: auto;
  }
  .log {
    margin: 0;
    padding: var(--space-3) 0;
    list-style: none;
    min-height: 14rem;
    width: max-content;
    min-width: 100%;
  }
  .log li {
    padding: 0 var(--space-4);
    display: flex;
    gap: var(--space-3);
  }
  .log li .op {
    font-size: var(--fs-xs);
    color: var(--text-soft);
    font-family: var(--font-sans);
    width: 4.5em;
    flex: none;
    padding-top: 0.2em;
  }
  .log li code {
    white-space: pre;
  }
  .log li.new {
    background: var(--code-line-hl);
    border-left: 3px solid var(--code-line-bar);
    padding-left: calc(var(--space-4) - 3px);
  }
  .log li.footer {
    margin-top: var(--space-2);
    opacity: 0.85;
  }
  .actions {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-wrap: wrap;
  }
  .status {
    font-size: var(--fs-sm);
    color: var(--text-soft);
  }
  @media (max-width: 639px) {
    .log li {
      padding: 0 var(--space-3);
      gap: var(--space-2);
    }
    .log li.new {
      padding-left: calc(var(--space-3) - 3px);
    }
  }
</style>
